import { useCallback, useEffect, useState } from "react"; // Add useState
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../redux/store";
import Loading from "../../../components/loading";
import ToastComponent from "../../../components/toast";
import Button from "../../../components/button";
import paths from "../../../navigation/paths";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  courseByIdStateAction,
  getCourseByIdState,
  registerForCourseStateAction,
  getRegisterForCourseState,
  clearRegisterForCourseState,
} from "../../../redux/features/course-slice";
import { format } from "date-fns";
import { getUserState } from "../../../redux/features/auth-slice";
import Chip from "../../../components/chip";
import DOMPurify from "dompurify";

export default function UserCourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const courseState = useSelector(getCourseByIdState);
  const registerState = useSelector(getRegisterForCourseState);
  const errorState = useSelector(getErrorState);
  const userState = useSelector(getUserState);
  const [isRegistered, setIsRegistered] = useState(false);

  const createMarkup = (html: string) => {
    return {
      __html: DOMPurify.sanitize(html),
    };
  };

  const getButtonLabel = () => {
    if (courseState.data?.status !== "active") {
      return "Please wait until available";
    }
    if (isRegistered) {
      return "Already Registered";
    }
    if (courseState.data?.availability < 1) {
      return "Course is full";
    }
    if (registerState.loading) {
      return "Registering...";
    }
    return "Register";
  };

  const fetchCourseDetail = useCallback(async () => {
    if (id) {
      await dispatch(courseByIdStateAction({ id }));
    }
  }, [dispatch, id]);

  const handleRegister = useCallback(async () => {
    if (id) {
      await dispatch(
        registerForCourseStateAction({ id, userId: "current-user-id" })
      ); // Replace with actual user ID
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchCourseDetail();
  }, [fetchCourseDetail]);

  useEffect(() => {
    if (courseState.data) {
      dispatch(
        setBreadcrumbState([
          { id: "courses", path: paths.COURSES_USER, title: "Browse Courses" },
          { id: courseState.data.id, path: null, title: courseState.data.name },
        ])
      );
    }
  }, [courseState.data, dispatch]);

  useEffect(() => {
    if (errorState) {
      toast(errorState.message, { type: "error", autoClose: 5000 });
    }
  }, [errorState]);

  useEffect(() => {
    if (registerState.data) {
      toast("Successfully registered for the course!", {
        type: "success",
        autoClose: 5000,
      });
      dispatch(clearRegisterForCourseState());
      setIsRegistered(true); // Update isRegistered after successful registration
    }
  }, [registerState.data, dispatch]);

  // Check registration status when course or user data changes
  useEffect(() => {
    if (courseState.data && userState.data) {
      const registered =
        courseState.data.registeredUsers?.some(
          (registeredUser) => registeredUser.id === userState.data?.id
        ) || false;
      setIsRegistered(registered);
    }
  }, [courseState.data, userState.data]);

  return (
    <div className="h-full pl-10 flex flex-col overflow-y-auto">
      {courseState.loading ? (
        <div className="flex justify-center items-center h-64">
          <Loading />
        </div>
      ) : courseState.data ? (
        <>
          <Helmet title={courseState.data.name} />
          <article className="w-full">
            <img
              src={courseState.data.pictureUrl}
              alt={courseState.data.name}
              className="-full h-[33vh] md:w-2/3 md:h-[50vh] object-cover mb-6"
            />
            <div className="space-y-6">
              <div className="flex space-x-3 ">
                <h1 className="text-3xl font-bold text-gray-800">
                  {courseState.data.name}
                </h1>
                {courseState.data.availability < 1 && (
                  <Chip
                    label={"Full"}
                    type="default"
                    exClassName="w-fit h-fit font-bold"
                  />
                )}
              </div>
              <section className="space-y-2 w-2/3 sm:h-full md:h-full">
                <h2 className="text-xl font-semibold text-gray-700">
                  Description
                </h2>
                {courseState.data.description ? (
                  <div
                    className="ql-editor ql-snow"
                    dangerouslySetInnerHTML={createMarkup(
                      courseState.data.description
                    )}
                  />
                ) : (
                  <p className="text-gray-600">No description available.</p>
                )}
              </section>
              <section className="space-y-2 w-2/3 sm:h-full md:h-full">
                <h2 className="text-xl font-semibold text-gray-700">
                  Syllabus
                </h2>
                {courseState.data.syllabus ? (
                  <div
                    className="ql-editor ql-snow"
                    dangerouslySetInnerHTML={createMarkup(
                      courseState.data.syllabus
                    )}
                  />
                ) : (
                  <p className="text-gray-600">No syllabus available.</p>
                )}
              </section>

              <section className="space-y-2">
                <h2 className="text-xl font-semibold text-gray-700">Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-600">
                      <span className="font-medium">Authors:</span>{" "}
                      {courseState.data.authors
                        .map((author) => author.name)
                        .join(", ") || "No authors"}
                    </p>
                    <p className="text-gray-600 capitalize">
                      <span className="font-medium">Level:</span>{" "}
                      {courseState.data.level}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Duration:</span>{" "}
                      {courseState.data.duration} hours
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Capacity:</span>{" "}
                      {courseState.data.capacity} students
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">
                      <span className="font-medium">Prerequisite:</span>{" "}
                      {courseState.data.prerequisite || "None"}
                    </p>
                    <p className="text-gray-600 capitalize">
                      <span className="font-medium">Language:</span>{" "}
                      {courseState.data.language}
                    </p>
                    <p className="text-gray-600 capitalize">
                      <span className="font-medium">Status:</span>{" "}
                      {courseState.data.status}
                    </p>
                  </div>
                </div>
              </section>

              {courseState.data.schedule &&
                courseState.data.schedule.length > 0 && (
                  <section className="space-y-2">
                    <h2 className="text-xl font-semibold text-gray-700">
                      Schedule
                    </h2>
                    <ul className="list-disc list-inside text-gray-600">
                      {[...courseState.data.schedule]
                        .sort(
                          (a, b) =>
                            new Date(a).getTime() - new Date(b).getTime()
                        )
                        .map((date, index) => (
                          <li key={index}>{format(new Date(date), "PPp")}</li>
                        ))}
                    </ul>
                  </section>
                )}
              <div className="pt-4">
                <Button
                  label={getButtonLabel()}
                  onClick={handleRegister}
                  disabled={
                    registerState.loading ||
                    courseState.data.status !== "active" ||
                    isRegistered ||
                    courseState.data.availability < 1
                  }
                  exClassName="w-full sm:w-auto bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </article>
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-600">Course not found.</p>
        </div>
      )}
      <ToastComponent id="courseDetail" />
    </div>
  );
}
